import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

/**
 * Public quote page - `/q/<token>` (LEADS_MODULE_SCOPE.md s.16.3).
 *
 * No login, no app chrome, ONE action: Accept. The token is the key and
 * an optional password the second layer (sent in a header, never the
 * URL). Expired links say so and offer the contact form; the operator can
 * extend without re-sending.
 *
 * Deviation from the artboard, recorded: a quote in this app carries one
 * location and its line items, so the "site table" is rendered as one site
 * heading over the items; a multi-site quote shows one block per item
 * group.
 */

type View = {
  number: string;
  title: string;
  leadName: string;
  preparedAt: string;
  validUntil: string | null;
  serviceDate: string | null;
  site: { name: string; address: any } | null;
  items: Array<{ id: string; name: string; description: string | null; type: string; quantity: number; unit: string | null; price: number; lineTotal: number; group: number }>;
  total: number;
  /** Set when a deposit is required to schedule (on the quote or the lead). */
  depositPercent?: number | null;
  leadMarkupPercent: number | null;
  terms: string | null;
  accepted: boolean;
  acceptedAt: string | null;
  acceptedName: string | null;
  owner: { name: string; email: string; phone: string | null; title: string | null } | null;
};

const money = (n: number) => `$${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const longDate = (d: string | null) => (d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");

const font = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "1px solid #D9DEE6", borderRadius: 8, fontSize: 14, marginBottom: 10, fontFamily: font };
const btn: React.CSSProperties = { width: "100%", background: "#1a2332", color: "#fff", border: "none", borderRadius: 8, padding: "13px 18px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: font };
const ghost: React.CSSProperties = { background: "#fff", color: "#1a2332", border: "1px solid #D9DEE6", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: font };

const PublicQuote = () => {
  const { token } = useParams();
  const [probe, setProbe] = useState<{ exists: boolean; expired: boolean; needsPassword: boolean } | null>(null);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [accept, setAccept] = useState({ name: "", email: "", poNumber: "", signature: "" });
  const [done, setDone] = useState<{ acceptedAt: string } | null>(null);
  // Double confirm (Jack, 2026-10-01): the first click asks, the second accepts.
  const [confirming, setConfirming] = useState(false);

  const [asking, setAsking] = useState(false);
  const [q, setQ] = useState({ name: "", email: "", phone: "", message: "" });
  const [asked, setAsked] = useState(false);

  const headers = () => (password ? { "X-Share-Password": password } : {});

  useEffect(() => {
    document.title = "Quote | GetMulch";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`/public/q/${token}/probe`, { excludeInterceptor: true } as any)
      .then((r) => {
        setProbe(r.data);
        if (r.data?.exists && !r.data.expired && !r.data.needsPassword) load("");
      })
      .catch(() => setProbe({ exists: false, expired: false, needsPassword: false }));
  }, [token]); // eslint-disable-line

  const load = async (pw: string) => {
    setBusy(true);
    setPasswordError(null);
    try {
      const r = await axios.get(`/public/q/${token}`, { headers: pw ? { "X-Share-Password": pw } : {}, excludeInterceptor: true } as any);
      setView(r.data);
      if (r.data?.accepted) setDone({ acceptedAt: r.data.acceptedAt });
    } catch (e: any) {
      if (e?.response?.status === 401) setPasswordError("That password is not right.");
      else if (e?.response?.status === 429) setPasswordError("Too many attempts. Please wait a few minutes and try again.");
      else if (e?.response?.status === 410) setProbe((p) => ({ ...(p ?? { exists: true, needsPassword: false }), expired: true } as any));
      else setError(e?.response?.data?.message ?? "This quote could not be loaded.");
    }
    setBusy(false);
  };

  const submitAccept = async () => {
    if (!view || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(`/public/q/${token}/accept`, accept, { headers: headers(), excludeInterceptor: true } as any);
      setDone({ acceptedAt: r.data?.acceptedAt ?? new Date().toISOString() });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "The quote could not be accepted. Please contact us.");
    }
    setBusy(false);
  };

  const submitQuestion = async () => {
    setBusy(true);
    setError(null);
    try {
      await axios.post(`/public/q/${token}/question`, q, { headers: headers(), excludeInterceptor: true } as any);
      setAsked(true);
      setAsking(false);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Your question could not be sent.");
    }
    setBusy(false);
  };

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`/public/q/${token}/pdf`, { headers: headers(), excludeInterceptor: true } as any);
      const data: string = r.data?.result;
      if (!data) return;
      const a = document.createElement("a");
      a.href = data;
      a.download = `${view?.number ?? "quote"}.pdf`;
      a.click();
    } catch {
      setError("The PDF could not be generated.");
    }
  };

  const shell = (children: React.ReactNode) => (
    <div style={{ minHeight: "100vh", background: "#F4F6F8", fontFamily: font, color: "#1a2332" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #E3E8EF" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src="/images/logo-dark.png" alt="GetMulch" style={{ height: 26, width: "auto", display: "block" }} />
            {view && (
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Quote {view.number} · {view.leadName}</div>
                <div style={{ fontSize: 12, color: "#5D5E62" }}>
                  Prepared {longDate(view.preparedAt)}{view.validUntil ? ` \u00b7 valid until ${longDate(view.validUntil)}` : ""}
                </div>
              </div>
            )}
          </div>
          {view && (
            <div style={{ display: "flex", gap: 8 }}>
              <button style={ghost} onClick={downloadPdf}>Download PDF</button>
              <button style={ghost} onClick={() => setAsking((v) => !v)}>Ask a question</button>
            </div>
          )}
        </div>
      </div>
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "22px 20px" }}>{children}</div>
    </div>
  );

  if (!probe) return shell(<div style={{ color: "#5D5E62" }}>Loading…</div>);

  if (!probe.exists) {
    return shell(
      <div style={{ background: "#fff", border: "1px solid #E3E8EF", borderRadius: 10, padding: 24, maxWidth: 560 }}>
        <h2 style={{ margin: "0 0 8px" }}>This link is not available</h2>
        <p style={{ color: "#5D5E62", margin: 0 }}>It may have been replaced by a newer quote. Please contact the person who sent it to you.</p>
      </div>,
    );
  }

  if (probe.expired && !view) {
    return shell(
      <div style={{ background: "#fff", border: "1px solid #E3E8EF", borderRadius: 10, padding: 24, maxWidth: 560 }}>
        <h2 style={{ margin: "0 0 8px" }}>This quote has expired</h2>
        <p style={{ color: "#5D5E62" }}>Pricing is held for 30 days. Ask us to refresh it and we will send a current quote.</p>
        {asked ? (
          <p><b>Thank you — we will be in touch shortly.</b></p>
        ) : (
          <>
            <input style={input} placeholder="Your name" value={q.name} onChange={(e) => setQ({ ...q, name: e.target.value })} />
            <input style={input} placeholder="Email" value={q.email} onChange={(e) => setQ({ ...q, email: e.target.value })} />
            <textarea style={{ ...input, minHeight: 90 }} placeholder="What would you like us to do?" value={q.message} onChange={(e) => setQ({ ...q, message: e.target.value })} />
            {error && <p style={{ color: "#A33B3A" }}>{error}</p>}
            <button style={btn} disabled={busy} onClick={submitQuestion}>Send</button>
          </>
        )}
      </div>,
    );
  }

  if (probe.needsPassword && !view) {
    return shell(
      <div style={{ background: "#fff", border: "1px solid #E3E8EF", borderRadius: 10, padding: 24, maxWidth: 440 }}>
        <h2 style={{ margin: "0 0 6px" }}>This quote is password protected</h2>
        <p style={{ color: "#5D5E62", marginTop: 0 }}>The password is in the email that brought you here.</p>
        <input style={input} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(password)} autoFocus />
        {passwordError && <p style={{ color: "#A33B3A", marginTop: 0 }}>{passwordError}</p>}
        <button style={btn} disabled={busy || !password} onClick={() => load(password)}>Open the quote</button>
      </div>,
    );
  }

  if (!view) return shell(<div style={{ color: "#5D5E62" }}>{error ?? "Loading\u2026"}</div>);

  const groups = Array.from(new Set(view.items.map((i) => i.group))).sort();
  const addr = view.site?.address ?? {};
  const addrLine = typeof addr === "string" ? addr : [addr.line1, addr.city, addr.state].filter(Boolean).join(", ");

  return shell(
    <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 560px", background: "#fff", border: "1px solid #E3E8EF", borderRadius: 10, padding: 24 }}>
        <h1 style={{ margin: "0 0 4px", fontSize: 22 }}>{view.title}</h1>
        {view.site && (
          <p style={{ margin: "0 0 18px", color: "#5D5E62", fontSize: 14 }}>
            {view.site.name}{addrLine ? ` \u00b7 ${addrLine}` : ""}{view.serviceDate ? ` \u00b7 target service ${longDate(view.serviceDate)}` : ""}
          </p>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ color: "#818181", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <th style={{ textAlign: "left", padding: "8px 0", borderBottom: "1px solid #E3E8EF" }}>Item</th>
              <th style={{ textAlign: "right", padding: "8px 0", borderBottom: "1px solid #E3E8EF" }}>Qty</th>
              <th style={{ textAlign: "right", padding: "8px 0", borderBottom: "1px solid #E3E8EF" }}>Rate</th>
              <th style={{ textAlign: "right", padding: "8px 0", borderBottom: "1px solid #E3E8EF" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g}>
                {groups.length > 1 && (
                  <tr><td colSpan={4} style={{ padding: "12px 0 4px", fontWeight: 700, fontSize: 12, color: "#5D5E62" }}>Section {Number(g) + 1}</td></tr>
                )}
                {view.items.filter((i) => i.group === g).map((i) => (
                  <tr key={i.id}>
                    <td style={{ padding: "10px 0", borderBottom: "1px solid #EEF1F5" }}>
                      <div style={{ fontWeight: 600 }}>{i.name}</div>
                      {i.description && <div style={{ fontSize: 12, color: "#818181" }}>{i.description}</div>}
                    </td>
                    <td style={{ textAlign: "right", padding: "10px 0", borderBottom: "1px solid #EEF1F5", whiteSpace: "nowrap" }}>{i.quantity}{i.unit ? ` ${i.unit}` : ""}</td>
                    <td style={{ textAlign: "right", padding: "10px 0", borderBottom: "1px solid #EEF1F5" }}>{money(i.price)}</td>
                    <td style={{ textAlign: "right", padding: "10px 0", borderBottom: "1px solid #EEF1F5", fontWeight: 600 }}>{money(i.lineTotal)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            <tr>
              <td colSpan={3} style={{ textAlign: "right", padding: "14px 0 4px", fontWeight: 700 }}>Total</td>
              <td style={{ textAlign: "right", padding: "14px 0 4px", fontWeight: 800, fontSize: 18 }}>{money(view.total)}</td>
            </tr>
            {view.depositPercent ? (
              <tr>
                <td colSpan={3} style={{ textAlign: "right", padding: "2px 0 4px", fontWeight: 600, fontSize: 13 }}>
                  {view.depositPercent}% deposit required to schedule
                </td>
                <td style={{ textAlign: "right", padding: "2px 0 4px", fontWeight: 700, fontSize: 13 }}>
                  {money(Math.round(view.total * view.depositPercent) / 100)}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {view.terms ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#818181", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Terms &amp; conditions</div>
            <p style={{ fontSize: 12, color: "#5D5E62", margin: 0, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{view.terms}</p>
          </div>
        ) : null}
        {view.leadMarkupPercent != null && view.leadMarkupPercent > 0 && (
          <p style={{ fontSize: 12, color: "#5D5E62", margin: "8px 0 0", background: "#F4F6F8", padding: "10px 12px", borderRadius: 8 }}>
            Registered client pricing is lower. Become a client at any commitment level and this quote is re-issued at client rates — ask us about a maintenance plan for this site or a volume program across your locations.
          </p>
        )}

        {asking && !asked && (
          <div style={{ marginTop: 18, borderTop: "1px solid #E3E8EF", paddingTop: 16 }}>
            <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Ask a question</h3>
            <input style={input} placeholder="Your name" value={q.name} onChange={(e) => setQ({ ...q, name: e.target.value })} />
            <input style={input} placeholder="Email" value={q.email} onChange={(e) => setQ({ ...q, email: e.target.value })} />
            <input style={input} placeholder="Phone (optional)" value={q.phone} onChange={(e) => setQ({ ...q, phone: e.target.value })} />
            <textarea style={{ ...input, minHeight: 90 }} placeholder="Your question" value={q.message} onChange={(e) => setQ({ ...q, message: e.target.value })} />
            <button style={{ ...btn, width: "auto" }} disabled={busy} onClick={submitQuestion}>Send</button>
          </div>
        )}
        {asked && <p style={{ marginTop: 16, fontWeight: 600 }}>Thanks — {view.owner?.name ?? "we"} will get back to you.</p>}
      </div>

      <div style={{ flex: "0 1 360px", minWidth: 300, background: "#fff", border: "1px solid #E3E8EF", borderRadius: 10, padding: 24, position: "sticky", top: 16 }}>
        {done ? (
          <>
            <h2 style={{ margin: "0 0 6px", fontSize: 18 }}>Accepted</h2>
            <p style={{ color: "#5D5E62", fontSize: 14 }}>
              {view.acceptedName ? `${view.acceptedName} accepted this quote on ` : "Accepted on "}{longDate(done.acceptedAt)}. A confirmation is on its way by email.
            </p>
            {/* What happens next (2026-10-08): a lead has no login yet, so say
                how they get access and how to pay, instead of leaving them
                waiting for an invoice they cannot pay. */}
            <div style={{ border: "1px solid #E3E8EF", borderRadius: 8, padding: 14, background: "#F8FAFC", marginTop: 12 }}>
              <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>What happens next</p>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#3D3E42", lineHeight: 1.5 }}>
                <li>{view.owner?.name ? `${view.owner.name} will` : "We will"} email you a link to set up your account, usually within one business day.</li>
                {view.depositPercent ? (
                  <li>{`Log in and pay the ${view.depositPercent}% deposit (${money(Math.round(view.total * view.depositPercent) / 100)}) to schedule the work.`}</li>
                ) : (
                  <li>Your invoice follows by email once the work is scheduled; you can pay it from your account.</li>
                )}
              </ol>
              {view.owner?.email && (
                <p style={{ margin: "10px 0 0", fontSize: 12, color: "#5D5E62" }}>
                  {`Need access sooner? Email ${view.owner.email}${view.owner.phone ? ` or call ${view.owner.phone}` : ""}.`}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <h2 style={{ margin: "0 0 6px", fontSize: 18 }}>Accept this quote</h2>
            <p style={{ color: "#5D5E62", fontSize: 13, marginTop: 0 }}>No account needed to accept. Accepting schedules the work and creates your invoice; you will get a confirmation by email, and we will set up an account so you can pay online.</p>
            <input style={input} placeholder="Your name" value={accept.name} onChange={(e) => setAccept({ ...accept, name: e.target.value })} />
            <input style={input} placeholder="Email" type="email" value={accept.email} onChange={(e) => setAccept({ ...accept, email: e.target.value })} />
            <input style={input} placeholder="Your PO number (optional)" value={accept.poNumber} onChange={(e) => setAccept({ ...accept, poNumber: e.target.value })} />
            <input style={{ ...input, fontStyle: "italic" }} placeholder="Type your name to sign" value={accept.signature} onChange={(e) => setAccept({ ...accept, signature: e.target.value })} />
            {error && <p style={{ color: "#A33B3A", fontSize: 13 }}>{error}</p>}
            {!confirming ? (
              <button style={btn} disabled={busy || !accept.name || !accept.email || !accept.signature} onClick={() => setConfirming(true)}>
                {`Accept ${money(view.total)}`}
              </button>
            ) : (
              <div style={{ border: "1px solid #D9DEE6", borderRadius: 8, padding: 14, background: "#F8FAFC" }}>
                <p style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 600 }}>Confirm acceptance</p>
                <p style={{ margin: "0 0 12px", fontSize: 13, color: "#5D5E62" }}>
                  {accept.name}, you are accepting quote {view.number} for {money(view.total)}
                  {view.depositPercent ? `, with a ${view.depositPercent}% deposit (${money(Math.round(view.total * view.depositPercent) / 100)}) required to schedule` : ""}.
                </p>
                <button style={btn} disabled={busy} onClick={submitAccept}>
                  {busy ? "Working\u2026" : "Yes, accept this quote"}
                </button>
                <button style={{ ...ghost, width: "100%", marginTop: 8 }} disabled={busy} onClick={() => setConfirming(false)}>
                  Go back
                </button>
              </div>
            )}
          </>
        )}
        {view.owner && (
          <p style={{ fontSize: 12, color: "#5D5E62", marginTop: 16, marginBottom: 0 }}>
            Questions first? {view.owner.name}{view.owner.phone ? ` \u00b7 ${view.owner.phone}` : ""} · <a href={`mailto:${view.owner.email}`} style={{ color: "#2F6FB5" }}>{view.owner.email}</a>
          </p>
        )}
      </div>
    </div>,
  );
};

export default PublicQuote;
